import { getStoredReferral } from '@/lib/referral';
import { getFunctionErrorMessage } from '@/lib/functionError';
import { useEnterpriseAccess } from '@/hooks/useEnterpriseAccess';
import { formatContractDate } from '@/lib/orgContract';
import { SEO } from "@/components/SEO";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  CheckCircle, 
  Sparkles, 
  Shield, 
  Award, 
  Users, 
  Zap, 
  TrendingUp, 
  BookOpen,
  MessageCircle,
  Target,
  Star,
  Building2,
  GraduationCap,
  ArrowRight,
  Rocket,
  Loader2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLicensePrice } from "@/hooks/useLicensePrice";
import { DiscountCodeInput, type DiscountResult } from "@/components/DiscountCodeInput";
import { useAuth } from "@/contexts/AuthContext";
import { useUserAccess } from "@/hooks/useUserAccess";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { checkSubscriptionStatus } from "@/lib/checkSubscription";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const PlansPage = () => {
  const navigate = useNavigate();
  const { label: proLabel, periodLabel: proPeriod, price: proPrice, currency: proCurrency } = useLicensePrice();
  const { user } = useAuth();
  const { hasPaidAccess } = useUserAccess();
  const [isProcessing, setIsProcessing] = useState(false);
  // Descuento aplicado (incluye el descuento por referido, auto-aplicado por DiscountCodeInput).
  const [proDiscount, setProDiscount] = useState<DiscountResult | null>(null);
  const fmtMoney = (amount: number, currency?: string) => {
    try {
      return new Intl.NumberFormat('es-CO', { style: 'currency', currency: (currency || 'USD').toUpperCase(), maximumFractionDigits: 0 }).format(amount);
    } catch {
      return `$${Math.round(amount).toLocaleString('es-CO')}`;
    }
  };

  // Check active licenses (only manual/enterprise count from local table)
  const { data: activeLicenses } = useQuery({
    queryKey: ['user_active_licenses_plans', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('user_subscriptions')
        .select('status, provider, billing_plans!inner(plan_type, name)')
        .eq('user_id', user.id)
        .eq('status', 'active');
      return (data || []).map((s: any) => ({
        plan_type: s.billing_plans?.plan_type || 'unknown',
        provider: s.provider ?? null,
      }));
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  });

  // Stripe truth (already filtered by configured price in check-subscription)
  const { data: subscriptionInfo } = useQuery({
    queryKey: ['check_subscription_plans', user?.id],
    queryFn: async () => {
      const data = await checkSubscriptionStatus();
      return data as { subscribed?: boolean } | null;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2,
    retry: 1,
  });

  // Cualquier suscripción Pro activa registrada en la plataforma (manual, cortesía o Stripe)
  const hasDbPro = activeLicenses?.some(l => l.plan_type === 'subscription');
  const hasProLicense = hasDbPro || subscriptionInfo?.subscribed === true;
  const { data: entAccess } = useEnterpriseAccess();
  const hasEnterpriseLicense = !!entAccess?.grantsAccess;
  const enterpriseExpired = !!entAccess?.isExpired;

  // Payment gateway config
  const { data: gatewayConfig } = useQuery({
    queryKey: ['payment_gateway_config_plans'],
    queryFn: async () => {
      const { data } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'payment_gateway')
        .maybeSingle();
      return (data?.value as any) ?? null;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 10,
  });

  const handleProCheckout = async () => {
    if (!user) {
      navigate("/auth?mode=signup&redirect=planes");
      return;
    }
    if (hasProLicense) return;

    const paymentsEnabled = gatewayConfig?.enabled ?? false;
    if (!paymentsEnabled) {
      toast.info("Los pagos no están habilitados en este momento.");
      return;
    }

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-license-checkout', {
        body: { discount_code_id: proDiscount?.code_id ?? null, referral_code: getStoredReferral() },
      });
      if (error) throw error;

      if (data?.warning === 'already_has_stripe_subscription' || data?.warning === 'already_has_pro') {
        toast.info(data.message || "Ya tienes una suscripción activa.");
        return;
      }
      if (data?.warning === 'already_has_enterprise') {
        const confirm = window.confirm(data.message);
        if (!confirm) return;
        const { data: d2, error: e2 } = await supabase.functions.invoke('create-license-checkout', {
          body: { discount_code_id: proDiscount?.code_id ?? null, force: true, referral_code: getStoredReferral() },
        });
        if (e2) throw e2;
        if (d2?.url) { window.location.href = d2.url; return; }
      }
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      toast.error(await getFunctionErrorMessage(err, "Error al iniciar el pago"));
    } finally {
      setIsProcessing(false);
    }
  };

  const benefits = [
    {
      icon: BookOpen,
      title: "Contenido actualizado constantemente",
      description: "Nuevos módulos y recursos cada semana para mantenerte al día con las últimas tendencias en IA"
    },
    {
      icon: GraduationCap,
      title: "Certificaciones reconocidas",
      description: "Credenciales digitales verificables que puedes compartir en LinkedIn y tu portafolio profesional"
    },
    {
      icon: MessageCircle,
      title: "Asistente LIA 24/7",
      description: "Inteligencia artificial personalizada que te guía, responde dudas y te ayuda a aplicar lo aprendido"
    },
    {
      icon: Users,
      title: "Comunidad activa",
      description: "Conecta con +500 profesionales de LATAM que comparten experiencias y oportunidades"
    },
    {
      icon: Target,
      title: "Proyectos prácticos",
      description: "Aplica lo aprendido en casos reales. El 80% del contenido es práctico y aplicable"
    },
    {
      icon: TrendingUp,
      title: "Seguimiento de progreso",
      description: "Métricas detalladas de tu avance, logros desbloqueados y recomendaciones personalizadas"
    }
  ];

  const stats = [
    { value: "500+", label: "Profesionales certificados" },
    { value: "14", label: "Certificaciones disponibles" },
    { value: "95%", label: "Tasa de finalización" },
    { value: "4.9/5", label: "Satisfacción promedio" }
  ];

  const faqs = [
    { 
      q: "¿Cuál es la diferencia entre la suscripción y las rutas?", 
      a: "La Licencia Pro te da acceso a contenido de la plataforma, comunidad y herramientas. Las rutas son experiencias intensivas con sesiones en vivo, mentoría y certificación, que se compran por separado según la cohorte que elijas."
    },
    { 
      q: "¿Puedo comprar una ruta sin tener suscripción?", 
      a: "Sí. Puedes comprar acceso a cualquier ruta pública directamente. La licencia es independiente y complementaria: te da acceso a contenido adicional de la plataforma." 
    },
    { 
      q: "¿Qué métodos de pago aceptan?", 
      a: "Aceptamos todas las tarjetas de crédito y débito principales (Visa, Mastercard, American Express), PayPal, y transferencias bancarias para planes Enterprise." 
    },
    { 
      q: "¿Hay garantía de devolución?", 
      a: "Ofrecemos garantía de satisfacción de 7 días en todos nuestros planes y rutas. Si no estás satisfecho, te devolvemos el 100% de tu inversión sin preguntas." 
    },
    { 
      q: "¿Cuánto tiempo tengo acceso al contenido de la ruta?", 
      a: "Mientras la cohorte esté activa, tienes acceso completo a todo el material. Las certificaciones que completes son tuyas de por vida." 
    },
    { 
      q: "¿Ofrecen descuentos para equipos?", 
      a: "¡Sí! Para equipos de 5+ personas ofrecemos cohortes empresariales con descuentos especiales. Contáctanos para una propuesta personalizada." 
    }
  ];

  const testimonials = [
    {
      name: "Carlos Mendoza",
      role: "Director de Tecnología",
      company: "FinTech Solutions",
      quote: "La ruta Líder IA fue la mejor inversión para mi carrera. En 3 meses ya estaba liderando proyectos de IA en mi empresa.",
      avatar: "CM"
    },
    {
      name: "Ana Lucía Torres",
      role: "Gerente de Innovación",
      company: "RetailCorp",
      quote: "La mentoría grupal y los proyectos prácticos marcaron la diferencia. No es solo teoría, es transformación real.",
      avatar: "AT"
    },
    {
      name: "Roberto Sánchez",
      role: "CEO",
      company: "StartupAI",
      quote: "Implementamos una cohorte empresarial para todo nuestro equipo. El ROI fue visible en menos de 2 meses.",
      avatar: "RS"
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Planes y certificaciones — LÍDER IA"
        description="Compara planes y rutas de certificación en IA. Encuentra el plan ideal para tu desarrollo profesional o tu empresa."
        path="/planes"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-primary/20 to-secondary/20 opacity-30 blur-[120px] rounded-full -translate-y-1/2" />
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <Badge variant="secondary" className="mb-4">Licencias y Certificaciones</Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Invierte en tu futuro con IA
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Suscríbete para acceder al contenido de la plataforma o compra una ruta intensivo 
            con sesiones en vivo, mentoría y certificación.
          </p>
          
          <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-secondary" />
              <span>Garantía 7 días</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-secondary" />
              <span>Cancela cuando quieras</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span>4.9/5 de satisfacción</span>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Section - 3 columns: Certificaciones | Licencia Pro (highlighted) | Enterprise */}
      <section className="py-8 max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-start">
          
          {/* Gratis (left) */}
          <Card className="relative overflow-hidden transition-all duration-300 hover:shadow-xl border-border/50 hover:border-primary/30">
            <div className="h-1.5 bg-gradient-to-r from-secondary to-secondary/70" />
            <CardContent className="p-6 md:p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-foreground mb-2">Gratis</h3>
                <div className="mb-3">
                  <span className="text-5xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">$0</span>
                  <span className="text-muted-foreground text-lg"> /siempre</span>
                </div>
                <p className="text-muted-foreground">Empieza a explorar la plataforma sin costo</p>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  "Acceso a contenido gratuito",
                  "Diagnóstico de madurez IA",
                  "Comunidad de aprendizaje",
                  "Asistente LIA (uso limitado)",
                  "Agente IA para Diagnóstico de un proceso e identificación de un proyecto IA",
                  "Tendencias IA",
                  "Catálogo de Herramientas IA",
                ].map((feature, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5 text-secondary" />
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              {user ? (
                <Button className="w-full text-base py-6 gap-2" variant="secondary" size="lg" disabled>
                  <CheckCircle className="w-4 h-4" />
                  Tu plan inicial
                </Button>
              ) : (
                <Button
                  className="w-full text-base py-6 gap-2"
                  variant="outline"
                  size="lg"
                  onClick={() => navigate("/auth?mode=signup")}
                >
                  <Sparkles className="w-4 h-4" />
                  Crear cuenta gratis
                  <ArrowRight className="w-4 h-4" />
                </Button>
              )}

              <p className="text-center text-sm text-muted-foreground mt-4">
                Sin tarjeta de crédito
              </p>
            </CardContent>
          </Card>

          {/* Licencia Pro - Highlighted (center) */}
          <Card className="relative overflow-hidden transition-all duration-300 hover:shadow-xl border-primary shadow-lg md:scale-105 z-10">
            <div className="h-1.5 bg-gradient-to-r from-primary to-primary/70" />
            <div className="absolute top-1.5 left-0 right-0 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground text-center py-2 text-sm font-semibold">
              {hasProLicense ? "✓ Tu plan actual" : "⭐ Recomendado"}
            </div>
            <CardContent className="p-6 md:p-8 pt-14">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-foreground mb-2">Licencia Pro</h3>
                <div className="mb-3">
                  <span className="text-5xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">{proLabel}</span>
                   <span className="text-muted-foreground text-lg">{proPeriod}</span>
                </div>
                <p className="text-muted-foreground">Acceso completo a la plataforma</p>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  "Acceso a todo el contenido",
                  "Rutas de autoaprendizaje a tu ritmo",
                  "Diagnóstico de nivel IA",
                  "Comunidad de aprendizaje",
                  "Asistente LIA completo",
                  "Soporte prioritario",
                  "Tendencias IA y Catalogo de Herramientas",
                  "Métricas y progreso",
                  "Actualizaciones constantes"
                ].map((feature, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5 text-primary" />
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              {user && !hasProLicense && proPrice != null && (
                <div className="mb-6 space-y-3">
                  <DiscountCodeInput
                    originalPrice={Number(proPrice)}
                    context="subscription"
                    onApply={setProDiscount}
                  />
                  {proDiscount && (
                    <div className="rounded-lg border p-3 text-sm space-y-1 bg-muted/30">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Valor licencia</span>
                        <span>{fmtMoney(Number(proPrice), proCurrency)}</span>
                      </div>
                      <div className="flex justify-between text-emerald-600">
                        <span>Descuento por referido</span>
                        <span>-{fmtMoney(proDiscount.calculated_discount, proCurrency)}</span>
                      </div>
                      <div className="flex justify-between font-semibold border-t border-border pt-1">
                        <span>Total a pagar</span>
                        <span>{fmtMoney(proDiscount.final_price, proCurrency)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {hasProLicense ? (
                <Button
                  className="w-full text-base py-6 gap-2"
                  variant="secondary"
                  size="lg"
                  disabled
                >
                  <CheckCircle className="w-4 h-4" />
                  Plan activo
                </Button>
              ) : (
                <Button 
                  className="w-full text-base py-6 shadow-lg gap-2"
                  variant="default" 
                  size="lg"
                  onClick={handleProCheckout}
                  disabled={isProcessing}
                >
                  {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {user ? "Adquirir Licencia Pro" : "Comenzar ahora"}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Enterprise (right) */}
          <Card className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl ${hasEnterpriseLicense ? 'border-emerald-500 shadow-lg' : 'border-border/50 hover:border-primary/30'}`}>
            <div className={`h-1.5 bg-gradient-to-r ${hasEnterpriseLicense ? 'from-emerald-500 to-emerald-400' : 'from-slate-500 to-slate-400'}`} />
            {hasEnterpriseLicense && (
              <div className="absolute top-1.5 left-0 right-0 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white text-center py-2 text-sm font-semibold">
                ✓ Tu plan actual
              </div>
            )}
            {enterpriseExpired && !hasEnterpriseLicense && (
              <div className="absolute top-1.5 left-0 right-0 bg-muted text-muted-foreground text-center py-2 text-sm font-semibold">
                Plan de tu empresa vencido el {formatContractDate(entAccess?.contractEndDate)}
              </div>
            )}
            <CardContent className={`p-6 md:p-8 ${hasEnterpriseLicense || enterpriseExpired ? 'pt-14' : ''}`}>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-foreground mb-2">Enterprise</h3>
                <div className="mb-3">
                  <span className="text-5xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">Custom</span>
                </div>
                <p className="text-muted-foreground">Soluciones a medida para equipos y empresas</p>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  "Modulos de Estratégia, Governanza IA y Gestión del Cambio",
                  "Capacitación in-house",
                  "Mentoría dedicada 1:1",
                  "Dashboard de métricas empresarial",
                  "Soporte prioritario 24/7",
                  "Integraciones personalizadas",
                  "Reportes ejecutivos",
                  "Account manager dedicado"
                ].map((feature, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5 text-muted-foreground" />
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button 
                className="w-full text-base py-6"
                variant="outline" 
                size="lg"
                onClick={() => navigate("/empresas")}
              >
                <Building2 className="w-4 h-4 mr-2" />
                Contactar ventas
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-8">
          <p className="text-muted-foreground">
            ¿Necesitas una solución para tu empresa? {" "}
            <Button variant="link" className="text-primary p-0 h-auto" onClick={() => navigate("/empresas")}>
              Conoce nuestras soluciones empresariales →
            </Button>
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
                  {stat.value}
                </p>
                <p className="text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">¿Por qué elegirnos?</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Todo lo que necesitas para liderar con IA
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Más que cursos: un ecosistema completo de aprendizaje, práctica y comunidad
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) => (
              <Card key={i} className="border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <benefit.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-foreground text-lg mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">Testimonios</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Lo que dicen nuestros graduados
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, i) => (
              <Card key={i} className="border-border/50 bg-card/80 backdrop-blur">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[1,2,3,4,5].map(j => (
                      <Star key={j} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <blockquote className="text-foreground mb-6 italic">
                    "{testimonial.quote}"
                  </blockquote>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-semibold text-sm">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}, {testimonial.company}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">Comparación</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              ¿Qué incluye cada opción?
            </h2>
          </div>

          <Card className="border-border/50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                 <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left p-4 font-semibold text-foreground">Característica</th>
                    <th className="text-center p-4 font-semibold text-foreground">Gratis</th>
                    <th className="text-center p-4 font-semibold text-foreground bg-primary/5">Licencia Pro</th>
                    <th className="text-center p-4 font-semibold text-foreground">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: "Contenido plataforma", cert: "Limitado", pro: "Todo", enterprise: "Todo" },
                    { feature: "Sesiones en Vivo", cert: "—", pro: "—", enterprise: "✓ + In-house" },
                    { feature: "Proyectos prácticos", cert: "—", pro: "Autoguiados", enterprise: "Personalizados" },
                    { feature: "Asistente LIA", cert: "Limitado", pro: "Completo", enterprise: "Enterprise" },
                    { feature: "Mentoría", cert: "—", pro: "—", enterprise: "1:1 Dedicada" },
                    { feature: "Certificación", cert: "—", pro: "—", enterprise: "✓ Custom" },
                    { feature: "Demo Day", cert: "—", pro: "—", enterprise: "✓ Privado" },
                    { feature: "Soporte", cert: "Comunidad", pro: "Prioritario", enterprise: "24/7 Dedicado" },
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-border/30 hover:bg-muted/30 transition-colors">
                      <td className="p-4 text-foreground font-medium">{row.feature}</td>
                      <td className="p-4 text-center text-muted-foreground">{row.cert}</td>
                      <td className="p-4 text-center bg-primary/5 font-medium text-foreground">{row.pro}</td>
                      <td className="p-4 text-center text-muted-foreground">{row.enterprise}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">FAQ</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-muted-foreground">
              Todo lo que necesitas saber antes de empezar
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, i) => (
              <AccordionItem 
                key={i} 
                value={`faq-${i}`} 
                className="bg-card border border-border/50 rounded-lg px-6 data-[state=open]:border-primary/30 transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-8">
            <p className="text-muted-foreground mb-4">¿Tienes más preguntas?</p>
            <Button variant="outline" onClick={() => navigate("/soporte")}>
              <MessageCircle className="w-4 h-4 mr-2" />
              Contáctanos
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Award className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Comienza tu transformación hoy
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Únete a más de 500 profesionales que ya están liderando la adopción de IA en sus organizaciones. 
            Explora nuestros rutas y elige tu próxima cohorte.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate("/rutas")} className="gap-2 text-base px-8">
              <Sparkles className="w-5 h-5" />
              Ver Certificaciones
            </Button>
            {hasProLicense ? (
              <Button size="lg" variant="outline" disabled className="text-base px-8 gap-2">
                <CheckCircle className="w-4 h-4" />
                Plan Pro activo
              </Button>
            ) : (
              <Button size="lg" variant="outline" onClick={handleProCheckout} disabled={isProcessing} className="text-base px-8">
                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Adquirir Licencia Pro
              </Button>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-6">
            <Shield className="w-4 h-4 inline mr-1" />
            Garantía de satisfacción de 7 días • Sin compromiso • Cancela cuando quieras
          </p>
        </div>
      </section>
    </div>
  );
};

export default PlansPage;
